import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  XMarkIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { SpotManagementService, NearbySpot } from "@/services/SpotManagementService";
import { useAuth } from "@/context/AuthContext";

interface AddSpotPanelProps {
  location: { lat: number; lng: number } | null;
  onCancel: () => void;
  onCreated: () => void;
  onNearbySpots?: (spots: NearbySpot[]) => void;
}

const MIN_NAME_LENGTH = 4;
const PROXIMITY_WARNING_METERS = 400;

const AddSpotPanel: React.FC<AddSpotPanelProps> = ({
  location,
  onCancel,
  onCreated,
  onNearbySpots,
}) => {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [country, setCountry] = useState<{ id: number; name: string } | null>(null);
  const [nearbySpots, setNearbySpots] = useState<NearbySpot[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingNearby, setIsLoadingNearby] = useState(false);

  const closestSpot = nearbySpots.length > 0 ? nearbySpots[0] : null;
  const tooClose = closestSpot != null && closestSpot.distance * 1000 < PROXIMITY_WARNING_METERS;
  const isValid = name.trim().length >= MIN_NAME_LENGTH && location != null && !tooClose;

  // Load nearby spots and country when location changes
  useEffect(() => {
    if (!location || !user?.accesstoken) return;

    let cancelled = false;

    const loadData = async () => {
      setIsLoadingNearby(true);

      const [nearby, countryResult] = await Promise.all([
        SpotManagementService.getNearbySpots(location.lat, location.lng, 0, 5),
        SpotManagementService.getCountryByLocation(location.lat, location.lng, user.accesstoken),
      ]);

      if (cancelled) return;

      setNearbySpots(nearby);
      onNearbySpots?.(nearby);
      setCountry(countryResult);
      setIsLoadingNearby(false);
    };

    loadData();
    return () => { cancelled = true; };
  }, [location?.lat, location?.lng, user?.accesstoken]);

  const handleCreate = useCallback(async () => {
    if (!isValid || !location || !user?.accesstoken) return;

    setIsCreating(true);
    setError(null);

    const result = await SpotManagementService.createSpot(
      user.accesstoken,
      name.trim(),
      location.lat,
      location.lng,
    );

    setIsCreating(false);

    if (result.success) {
      toast.success(`Spot "${name.trim()}" created`);
      onCreated();
    } else {
      setError(result.message || "Failed to create spot");
    }
  }, [isValid, location, name, user?.accesstoken, onCreated]);

  // Escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onCancel]);

  return (
    <div className="h-full flex flex-col bg-gray-900 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-3 bg-gray-800 border-b border-gray-700 flex items-center justify-between">
        <h2 className="text-white font-semibold flex items-center gap-2">
          <MapPinIcon className="h-5 w-5 text-green-400" />
          Add New Spot
        </h2>
        <button
          onClick={onCancel}
          className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
          title="Cancel (Escape)"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Instructions */}
        {!location && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
            <MapPinIcon className="h-8 w-8 text-blue-400 mx-auto mb-2" />
            <p className="text-blue-300 text-sm">
              Click on the map to place the spot
            </p>
          </div>
        )}

        {/* Coordinates */}
        {location && (
          <div className="bg-gray-800 rounded-lg p-3">
            <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Location</div>
            <div className="text-sm text-gray-300 font-mono">
              {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
            </div>
            {country && (
              <div className="mt-1 text-xs text-gray-400">
                {country.name}
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">Drag the marker to adjust</p>
          </div>
        )}

        {/* Proximity warning */}
        {tooClose && closestSpot && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm text-red-300 font-medium">Too close to existing spot</div>
              <div className="text-xs text-red-400 mt-1">
                "{closestSpot.name}" is only {Math.round(closestSpot.distance * 1000)}m away.
                Spots within {PROXIMITY_WARNING_METERS}m are considered the same location.
              </div>
            </div>
          </div>
        )}

        {/* Nearby spots */}
        {location && nearbySpots.length > 0 && !tooClose && (
          <div>
            <div className="text-xs text-gray-500 uppercase font-semibold mb-2">Nearby Spots</div>
            <div className="space-y-1">
              {nearbySpots.slice(0, 5).map((spot) => (
                <div
                  key={spot.id}
                  className="flex items-center justify-between text-xs bg-gray-800 rounded px-2 py-1.5"
                >
                  <span className="text-gray-300 truncate">{spot.name}</span>
                  <span className="text-gray-500 flex-shrink-0 ml-2">
                    {spot.distance < 1
                      ? `${Math.round(spot.distance * 1000)}m`
                      : `${spot.distance.toFixed(1)}km`
                    }
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Spot name */}
        {location && (
          <div>
            <label className="block text-xs text-gray-500 uppercase font-semibold mb-1">
              Spot Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null); }}
              placeholder="e.g. Sunset Beach, Donkey Lagoon"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              autoFocus
            />
            {name.length > 0 && name.trim().length < MIN_NAME_LENGTH && (
              <p className="text-xs text-yellow-400 mt-1">
                Name must be at least {MIN_NAME_LENGTH} characters
              </p>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
            {error}
          </div>
        )}
      </div>

      {/* Footer actions */}
      {location && (
        <div className="p-3 border-t border-gray-700 flex gap-2">
          <button
            onClick={onCancel}
            disabled={isCreating}
            className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!isValid || isCreating}
            className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isCreating && (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            Create Spot
          </button>
        </div>
      )}
    </div>
  );
};

export default AddSpotPanel;
